/**
 * Pure helper functions for building template configuration objects.
 *
 * Extracted from `buildConfigsFromResult` in VideoTemplatesContext to improve
 * readability and testability. All functions are stateless — they read from
 * the provided context objects and write into the mutable accumulators.
 */

import { CompositionNormalizer } from '@src/components/sidepanel/compositionNormalizer/compositionNormalizer';
import { KARAOKE_TRACK_NUMBER } from '@src/editor/constants';
import { BASE_POSITIONAL_DEFAULTS } from '@src/editor/models/Element';
import {
  isApproxEqual,
  isSourceValueApproxEqual,
} from '@src/utility/approximateEquality';
import { LogManager } from '@src/utility/Logger';
import parsePercent from '@src/utility/parsePercent';
import { convertToPixels } from '@src/utility/videoTranscriptionUtils';

import { getCompositionPlacementKeyByAspectRatio } from './videoTemplateUtils';

import type {
  AspectConfiguration,
  CompositionTemplate,
} from '../CompositionSettings/types';
import type {
  CaptionsConfiguration,
  CardConfiguration,
  HookConfiguration,
  LogoConfiguration,
  NamecardConfiguration,
  VideoTemplateConfig,
  VideoTemplateConfigured,
} from './types';
import type {
  VideoTemplateElementType,
  VideoTemplateSettings,
} from './VideoTemplatesContext';
import type { AspectRatio } from '@src/types.ts/video';

const logger = LogManager.getLogger('videoTemplate:buildTemplateConfigs');

// =============================================================================
// Shared types
// =============================================================================

/** Accumulated repo-formatted configs and local overrides built by the helpers. */
export type RepoConfigsResult = {
  repoConfigs: {
    logoConfiguration?: Record<string, unknown>;
    captionsConfiguration?: Record<string, unknown>;
    namecardConfiguration?: Record<string, unknown>;
    introCardConfiguration?: Record<string, unknown>;
    endCardConfiguration?: Record<string, unknown>;
    hookConfiguration?: Record<string, unknown>;
  };
  localOverrides: Partial<VideoTemplateConfig>;
};

/** Read-only snapshot of the active template and its runtime settings. */
export type TemplateConfigContext = {
  activeTemplate: VideoTemplateConfigured;
  activeTemplateSettings: VideoTemplateSettings;
  getCompositionById:
    | ((id: string) => CompositionTemplate | null | undefined)
    | undefined;
};

/**
 * Read-only accessors into live editor / renderer state.
 * Kept minimal so the helpers don't depend on full store types.
 */
export type EditorElementContext = {
  getElementById: (id: string) => any | undefined;
  karaokeLines: readonly {
    id: string;
    transcriptStartTime: number;
    transcriptEndTime: number;
  }[];
  defaultKaraokeConfig: any;
  getKaraokeConfigForKaraokeClipId: (id: string) => any;
  rendererGetElements: () => readonly any[];
  currentTime: number;
  videoSourceWidth: number;
  videoSourceHeight: number;
  /**
   * The DatoCMS record behind a karaoke template id — the shape a video
   * template's `captionsConfig` is read in. Deliberately not the matching
   * `brandingTemplates` entry: those are display copies whose colors are already
   * strings, and storing one as a config makes it un-re-appliable.
   */
  resolveKaraokeBrandingRecord: (id: string) => any | null;
  currentAspectRatio: AspectRatio;
};

/** Mutable output buckets that builders write into. */
export type ConfigAccumulators = {
  repoConfigs: RepoConfigsResult['repoConfigs'];
  localOverrides: Partial<VideoTemplateConfig>;
};

// =============================================================================
// Logo reference resolution (PLAT-9125 Bug 2 fix)
// =============================================================================

type LogoResolutionInput = {
  templateLogo: LogoConfiguration['logo'];
  replacedUrl: string | undefined;
  replacedUploadId: string | undefined;
  elementSourceUrl: string | undefined;
};

/**
 * Resolves the logo reference from multiple sources in priority order:
 * 1. Replacement URL + upload ID (user replaced the logo via photo modal)
 * 2. Current element source URL + template config's upload ID
 * 3. Template config's logo (original)
 */
export function resolveLogoReference({
  templateLogo,
  replacedUrl,
  replacedUploadId,
  elementSourceUrl,
}: LogoResolutionInput): LogoConfiguration['logo'] {
  if (replacedUrl && replacedUploadId) {
    return { id: replacedUploadId, url: replacedUrl };
  }
  if (elementSourceUrl && templateLogo?.id) {
    // Element source URL may differ from template config (e.g. after replacement)
    return { id: templateLogo.id, url: elementSourceUrl };
  }
  if (elementSourceUrl && !templateLogo) {
    // Logo was added but template has no logo config — source URL is all we have
    logger.warn(
      'Logo element exists but no template logo config or upload ID available',
      { source: elementSourceUrl },
    );
  }
  return templateLogo;
}

// =============================================================================
// Logo upload ID resolution (PLAT-9125 Bug 3 fix)
// =============================================================================

/**
 * Resolves the upload ID for a logo replacement from multiple sources:
 * 1. replacedLogoUploadId (set when logo was replaced via photo modal)
 * 2. Template config's logo ID (when logo came from existing template)
 */
export function resolveLogoUploadId(
  replacedLogoUploadId: string | undefined,
  templateLogoId: string | undefined,
): string | null {
  return replacedLogoUploadId ?? templateLogoId ?? null;
}

// =============================================================================
// Placement builders
// =============================================================================

type PlacementInput = {
  templateCtx: TemplateConfigContext;
  editorCtx: EditorElementContext;
  enabledChanges: Partial<Record<VideoTemplateElementType, boolean>>;
};

/**
 * Collects logo placement config from the live editor element.
 * Returns `'skip'` when the element isn't found (caller should `continue`).
 */
export function collectLogoPlacement(
  { templateCtx, editorCtx, enabledChanges }: PlacementInput,
  accumulators: ConfigAccumulators,
): 'skip' | void {
  const { activeTemplate, activeTemplateSettings } = templateCtx;
  const logoEntry = activeTemplateSettings.videoTemplateElements.find(
    e => e.type === 'logo',
  );
  if (!logoEntry) {
    logger.warn('No logo element found in template settings');
    return 'skip';
  }

  const sourceElement = editorCtx.getElementById(logoEntry.id) as
    | {
        source?: string;
        opacity: string;
        x: string;
        y: string;
        width: any;
        height: any;
        fit: string;
      }
    | undefined;

  if (!sourceElement) {
    logger.warn('Logo element not found in editor', {
      elementId: logoEntry.id,
    });
    return 'skip';
  }

  const logoEnabled =
    enabledChanges.logo ??
    activeTemplate.configuration?.logoConfiguration?.enabled ??
    true;

  const templateLogo =
    activeTemplate.configuration?.logoConfiguration?.logo ?? null;
  const replacedUrl = activeTemplateSettings.replacedElementIds?.logo;
  const replacedUploadId = activeTemplateSettings.replacedLogoUploadId;

  const logo = resolveLogoReference({
    templateLogo,
    replacedUrl,
    replacedUploadId,
    elementSourceUrl: sourceElement.source,
  });

  const logoConfig: LogoConfiguration = {
    enabled: logoEnabled,
    lockPosition:
      activeTemplate.configuration?.logoConfiguration?.lockPosition ?? false,
    logo,
    opacity: parseFloat(sourceElement.opacity) || null,
    placementX: parseFloat(sourceElement.x) || null,
    placementY: parseFloat(sourceElement.y) || null,
    width: parseFloat(sourceElement.width?.toString() || '0') || null,
    height: parseFloat(sourceElement.height?.toString() || '0') || null,
    fit: (sourceElement.fit as 'cover' | 'contain' | 'fill' | null) || null,
  };

  accumulators.repoConfigs.logoConfiguration = logoConfig;
  accumulators.localOverrides.logoConfiguration = logoConfig;
  logger.info('Logo placement collected', {
    logoConfig,
    logoSource: replacedUrl ? 'replacement' : 'template',
  });
}

/**
 * Collects captions (karaoke) placement config from live karaoke state.
 * Returns `'skip'` when no karaoke config is found.
 */
export function collectCaptionsPlacement(
  { templateCtx, editorCtx, enabledChanges }: PlacementInput,
  accumulators: ConfigAccumulators,
): 'skip' | void {
  const { activeTemplate } = templateCtx;
  const currentTime = editorCtx.currentTime;

  // Find the karaoke line at the current time, or the closest previous one
  const activeKaraokeElement =
    editorCtx.karaokeLines.find(
      line =>
        line.transcriptStartTime <= currentTime &&
        currentTime < line.transcriptEndTime,
    ) ??
    (
      editorCtx.karaokeLines as readonly {
        id: string;
        transcriptStartTime: number;
        transcriptEndTime: number;
      }[]
    ).reduce<
      | { id: string; transcriptStartTime: number; transcriptEndTime: number }
      | undefined
    >(
      (closest, line) =>
        line.transcriptEndTime <= currentTime &&
        (!closest || line.transcriptEndTime > closest.transcriptEndTime)
          ? line
          : closest,
      undefined,
    );

  // Renderer element matched to the time-based karaoke line for position (x/y)
  const karaokeRendererElement = activeKaraokeElement
    ? editorCtx
        .rendererGetElements()
        .find(
          (e: any) =>
            e.source?.id === activeKaraokeElement.id &&
            (e.track === KARAOKE_TRACK_NUMBER ||
              e.track === KARAOKE_TRACK_NUMBER + 1),
        )
    : undefined;

  const config = activeKaraokeElement
    ? editorCtx.getKaraokeConfigForKaraokeClipId(activeKaraokeElement.id)
    : editorCtx.defaultKaraokeConfig;

  if (!config) {
    logger.warn('No karaoke element found on track');
    return 'skip';
  }

  const xAnchor = config?.x_alignment as '0%' | '50%' | '100%' | null;
  let fontSize: number | null = null;
  if (config.font_size != null) {
    const fontSizeStr = String(config.font_size);
    const fontSizeValue = parseFloat(fontSizeStr);
    const fontSizeUnits = fontSizeStr.match(/[a-z]+/)?.[0] || 'px';
    fontSize =
      fontSizeUnits === 'px'
        ? fontSizeValue
        : Math.round(
            convertToPixels(fontSizeValue, fontSizeUnits, {
              width: editorCtx.videoSourceWidth,
              height: editorCtx.videoSourceHeight,
            }),
          );
  }

  const captionsEnabled =
    enabledChanges.captions ??
    activeTemplate.configuration?.captionsConfiguration?.enabled ??
    true;

  // Preserve explicit 0% as an override; only fall back to null when the
  // renderer element isn't present. Using `|| null` here would collapse a
  // user-placed 0% to null and let the karaoke preset's position win on the
  // next render (the bug PLAT-9823 patched on the read side).
  //
  // An axis the renderer omits is not unknown — Creatomate omits a property that
  // sits at its default, so the caption is at `BASE_POSITIONAL_DEFAULTS`. Storing
  // null for it loses a value we know, and the replacement merge then fills the
  // hole with the *previous* karaoke record's coordinate, moving the captions on
  // the next apply.
  const rendererY = karaokeRendererElement
    ? (karaokeRendererElement.source?.y ?? BASE_POSITIONAL_DEFAULTS.y)
    : undefined;
  const rendererX = karaokeRendererElement
    ? (karaokeRendererElement.source?.x ?? BASE_POSITIONAL_DEFAULTS.x)
    : undefined;
  const captionsLockPosition =
    activeTemplate.configuration?.captionsConfiguration?.lockPosition ?? false;
  const captionsConfig: CaptionsConfiguration = {
    enabled: captionsEnabled,
    lockPosition: captionsLockPosition,
    captionsConfig:
      activeTemplate.configuration?.captionsConfiguration?.captionsConfig ??
      null,
    verticalPosition: rendererY != null ? parseFloat(rendererY) : null,
    horizontalPosition: rendererX != null ? parseFloat(rendererX) : null,
    horizontalAlignment: xAnchor,
    fontSize,
  };

  const captionsConfigRecordId =
    activeTemplate.configuration?.captionsConfiguration?.captionsConfig?.id ??
    null;

  accumulators.repoConfigs.captionsConfiguration = {
    enabled: captionsEnabled,
    lockPosition: captionsLockPosition,
    captionsConfig: captionsConfigRecordId,
    verticalPosition: captionsConfig.verticalPosition,
    horizontalPosition: captionsConfig.horizontalPosition,
    horizontalAlignment: captionsConfig.horizontalAlignment,
    fontSize: captionsConfig.fontSize,
  };
  accumulators.localOverrides.captionsConfiguration = captionsConfig;
  logger.info('Captions placement collected', { captionsConfig });
}

/**
 * Per-axis scale of a live composition element against its template source —
 * `currentW/originalW` and `currentH/originalH` — as stored in a
 * `placementOverride`. Shared by the nametag and the hook (PLAT-11034) so the two
 * positionable overlays cannot drift on how scale is derived.
 *
 * The two axes are deliberately NOT collapsed into `Math.max(...)`: that didn't
 * merely lose the smaller axis, it wrote a value that is wrong on that axis. A
 * height-dominant resize stored the height ratio, and because the override is
 * merged into the composition's per-AR scale and then applied uniformly, the card
 * rendered far wider than intended (a real case reached 134% of the canvas). Two
 * axes make the captured state representable.
 *
 * Rounded to 5dp, matching the composition side (`CompositionNormalizer.roundScale`)
 * — the override is merged into that same per-AR scale slot, so a coarser value
 * here would make the two save paths disagree. Safe to store because
 * `placement_override` is a JSON field, not a rounded numeric column.
 */
function computeCompositionScale(
  compositionSource: Record<string, any> | undefined | null,
  sourceElement: { width?: unknown; height?: unknown },
  editorCtx: EditorElementContext,
): { scaleX: string | null; scaleY: string | null } {
  if (!compositionSource) return { scaleX: null, scaleY: null };

  const toPixels = (value: unknown, referenceDimension: number): number => {
    if (value == null) return 0;
    const str = value.toString();
    if (str.endsWith('%')) {
      return (parseFloat(str) / 100) * referenceDimension;
    }
    return parseFloat(str) || 0;
  };

  const videoWidth = editorCtx.videoSourceWidth;
  const videoHeight = editorCtx.videoSourceHeight;
  const originalWidth = toPixels(compositionSource.width, videoWidth);
  const originalHeight = toPixels(compositionSource.height, videoHeight);
  const currentWidth = toPixels(sourceElement.width, videoWidth);
  const currentHeight = toPixels(sourceElement.height, videoHeight);

  if (
    originalWidth <= 0 ||
    originalHeight <= 0 ||
    currentWidth <= 0 ||
    currentHeight <= 0
  ) {
    return { scaleX: null, scaleY: null };
  }

  const round = (value: number) => `${Math.round(value * 100000) / 100000}`;
  return {
    scaleX: round(currentWidth / originalWidth),
    scaleY: round(currentHeight / originalHeight),
  };
}

/**
 * Collects namecard placement config from the live editor element.
 * Returns `'skip'` when the element isn't found.
 */
export function collectNamecardPlacement(
  { templateCtx, editorCtx, enabledChanges }: PlacementInput,
  accumulators: ConfigAccumulators,
): 'skip' | void {
  const { activeTemplate, activeTemplateSettings } = templateCtx;

  const namecardEntry = activeTemplateSettings.videoTemplateElements.find(
    e => e.type === 'nameCard',
  );
  if (!namecardEntry) {
    logger.warn('No namecard element found in template settings');
    return 'skip';
  }

  const sourceElement = editorCtx.getElementById(namecardEntry.id);
  if (!sourceElement) {
    logger.warn('Namecard element not found in editor', {
      elementId: namecardEntry.id,
    });
    return 'skip';
  }

  const existingNamecardConfig =
    activeTemplate.configuration?.namecardConfiguration;

  const { scaleX, scaleY } = computeCompositionScale(
    existingNamecardConfig?.compositionTemplate?.source,
    sourceElement,
    editorCtx,
  );

  const capturedOverride: AspectConfiguration = {
    positionX: sourceElement.x?.toString() ?? null,
    positionY: sourceElement.y?.toString() ?? null,
    // `scale` is dual-written (= scaleX) so a client still on the previous bundle
    // renders uniformly rather than falling back to 1. Remove one release after
    // this ships.
    scale: scaleX,
    scaleX,
    scaleY,
  };

  // Drop the override when the live placement matches the composition's
  // baseline for the current AR. Without this, every save persists a
  // placementOverride equal to the AR baseline, which then deep-merges into
  // every other AR (placementOverride is single-valued) and clobbers their
  // baselines. Writing null here also clears any stale override in DatoCMS.
  const arBaseline =
    existingNamecardConfig?.compositionTemplate?.[
      getCompositionPlacementKeyByAspectRatio(editorCtx.currentAspectRatio)
    ] ?? null;
  const placementOverride: AspectConfiguration | null = matchesBaseline(
    capturedOverride,
    arBaseline,
  )
    ? null
    : capturedOverride;

  const namecardEnabled =
    enabledChanges.nameCard ?? existingNamecardConfig?.enabled ?? true;

  const namecardLockPosition = existingNamecardConfig?.lockPosition ?? false;
  const namecardConfig: NamecardConfiguration = {
    enabled: namecardEnabled,
    lockPosition: namecardLockPosition,
    compositionTemplate: existingNamecardConfig?.compositionTemplate ?? null,
    placementOverride,
  };

  const compositionTemplateId =
    existingNamecardConfig?.compositionTemplate?.id ?? null;

  accumulators.repoConfigs.namecardConfiguration = {
    enabled: namecardEnabled,
    lockPosition: namecardLockPosition,
    compositionTemplate: compositionTemplateId,
    placementOverride,
  };
  accumulators.localOverrides.namecardConfiguration = namecardConfig;
  logger.info('Namecard placement collected', { namecardConfig });
}

/**
 * PLAT-11034 — collects the hook's canvas placement from the live element.
 *
 * The hook is a positionable overlay, so it carries a `placementOverride` exactly
 * as the nametag does: capture where the creator left it, and drop the override
 * when it still matches the composition's baseline for this aspect ratio (writing
 * a baseline-equal override would deep-merge into the other aspect ratios and
 * clobber theirs — the same trap `collectNamecardPlacement` documents).
 *
 * Returns `'skip'` when the element isn't found.
 */
export function collectHookPlacement(
  { templateCtx, editorCtx, enabledChanges }: PlacementInput,
  accumulators: ConfigAccumulators,
): 'skip' | void {
  const { activeTemplate, activeTemplateSettings } = templateCtx;

  const hookEntry = activeTemplateSettings.videoTemplateElements.find(
    e => e.type === 'hook',
  );
  if (!hookEntry) {
    logger.warn('No hook element found in template settings');
    return 'skip';
  }

  const sourceElement = editorCtx.getElementById(hookEntry.id);
  if (!sourceElement) {
    logger.warn('Hook element not found in editor', {
      elementId: hookEntry.id,
    });
    return 'skip';
  }

  const existingHookConfig = activeTemplate.configuration?.hookConfiguration;

  const { scaleX, scaleY } = computeCompositionScale(
    existingHookConfig?.compositionTemplate?.source,
    sourceElement,
    editorCtx,
  );

  const capturedOverride: AspectConfiguration = {
    positionX: sourceElement.x?.toString() ?? null,
    positionY: sourceElement.y?.toString() ?? null,
    // `scale` is dual-written (= scaleX) so a client still on the previous bundle
    // renders uniformly rather than falling back to 1, matching the nametag.
    scale: scaleX,
    scaleX,
    scaleY,
  };

  const arBaseline =
    existingHookConfig?.compositionTemplate?.[
      getCompositionPlacementKeyByAspectRatio(editorCtx.currentAspectRatio)
    ] ?? null;
  const placementOverride: AspectConfiguration | null = matchesBaseline(
    capturedOverride,
    arBaseline,
  )
    ? null
    : capturedOverride;

  const hookEnabled =
    enabledChanges.hook ?? existingHookConfig?.enabled ?? true;
  const hookLockPosition = existingHookConfig?.lockPosition ?? false;
  const hookConfig: HookConfiguration = {
    enabled: hookEnabled,
    lockPosition: hookLockPosition,
    compositionTemplate: existingHookConfig?.compositionTemplate ?? null,
    duration: existingHookConfig?.duration ?? null,
    placementOverride,
  };

  accumulators.repoConfigs.hookConfiguration = {
    enabled: hookEnabled,
    lockPosition: hookLockPosition,
    compositionTemplate: existingHookConfig?.compositionTemplate?.id ?? null,
    duration: existingHookConfig?.duration ?? null,
    placementOverride,
  };
  accumulators.localOverrides.hookConfiguration = hookConfig;
  logger.info('Hook placement collected', { hookConfig });
}

/** Position tolerance — values are percent strings (e.g. "50%") parsed as raw numbers. */
const POSITION_MATCH_EPSILON = 0.1;
/**
 * Scale tolerance. Keep at >= 0.005: overrides saved before per-axis scales were
 * rounded to 2dp, and this is what lets a 5dp recapture of an unmoved card still
 * compare equal. Tightening it would mark every legacy override as deviated.
 */
const SCALE_MATCH_EPSILON = 0.005;

/** @visibleForTesting */
export function matchesBaseline(
  captured: AspectConfiguration,
  baseline: AspectConfiguration | null,
): boolean {
  if (!baseline) return false;
  // Scales are compared per axis AFTER resolving the legacy uniform `scale`.
  // Comparing the raw fields would pit a captured `scaleX` against a baseline
  // that stores only `scale` — isSourceValueApproxEqual treats concrete-vs-absent
  // as a difference, so every save would look like a deviation and persist an
  // override equal to the baseline. That override is single-valued and
  // deep-merges into the other ARs, clobbering their baselines.
  const capturedScale = CompositionNormalizer.resolveAxisScales(captured);
  const baselineScale = CompositionNormalizer.resolveAxisScales(baseline);
  return (
    isSourceValueApproxEqual(
      captured.positionX,
      baseline.positionX,
      POSITION_MATCH_EPSILON,
    ) &&
    isSourceValueApproxEqual(
      captured.positionY,
      baseline.positionY,
      POSITION_MATCH_EPSILON,
    ) &&
    isApproxEqual(
      capturedScale.scaleX,
      baselineScale.scaleX,
      SCALE_MATCH_EPSILON,
    ) &&
    isApproxEqual(
      capturedScale.scaleY,
      baselineScale.scaleY,
      SCALE_MATCH_EPSILON,
    )
  );
}

/**
 * Determines whether a freshly-collected placement config deviates from the
 * value already stored on the template — i.e. whether applying it would
 * actually change anything. Used to decide whether a placement toggle should
 * default ON in the update modal (and thus whether the "Update video template"
 * button should be active on open).
 *
 * Conservative by design: a numeric/string field only counts as deviated when
 * BOTH the collected and stored values are concrete and differ beyond
 * tolerance. When the template pins no value for a field (null), a concrete
 * collected value is treated as the rendered default — not a user deviation —
 * so the button stays inactive until the user genuinely moves something.
 * The one exception is a logo on a template that stores no logo configuration
 * at all — see the `logo` case.
 *
 * Expects the `localOverrides` produced by a `collect*Placement` call.
 */
export function placementDiffersFromStored(
  type: VideoTemplateElementType,
  produced: Partial<VideoTemplateConfig>,
  stored: VideoTemplateConfig | null | undefined,
): boolean {
  // Logo and caption placement fields are stored as integers in DatoCMS, so
  // the live (float) value is rounded on save. Compare at integer precision —
  // i.e. "would saving change the stored integer" — otherwise an unmoved
  // element reads as a permanent deviation after the first save (e.g. live
  // 96.4222 vs stored 96 once DatoCMS has rounded it).
  const valueDiffers = (
    a: string | number | null | undefined,
    b: string | number | null | undefined,
  ): boolean => {
    if (a == null || b == null) return false;
    const pa = parsePercent(a);
    const pb = parsePercent(b);
    if (pa == null || pb == null) return false;
    return Math.round(pa) !== Math.round(pb);
  };
  const stringDiffers = (
    a: string | null | undefined,
    b: string | null | undefined,
  ): boolean => a != null && b != null && a !== b;

  switch (type) {
    case 'logo': {
      const p = produced.logoConfiguration;
      if (!p) return false;
      const s = stored?.logoConfiguration;
      // A template with no logo configuration at all (fresh template, or a
      // logo added after the template was created) has nothing to compare
      // against, yet the live placement is unsaved. Without this the
      // null-tolerant comparisons below all read "no deviation", the modal's
      // "Save Logo Position" toggle defaults off, and an enabled-only save
      // writes a logo config with no asset, position or size (PLAT-11817).
      // A stored config whose fields are null is different: that is the
      // template pinning nothing on purpose, handled conservatively below.
      if (s == null) {
        return (
          p.placementX != null ||
          p.placementY != null ||
          p.width != null ||
          p.height != null
        );
      }
      return (
        valueDiffers(p.placementX, s?.placementX) ||
        valueDiffers(p.placementY, s?.placementY) ||
        valueDiffers(p.width, s?.width) ||
        valueDiffers(p.height, s?.height) ||
        valueDiffers(p.opacity, s?.opacity) ||
        stringDiffers(p.fit, s?.fit)
      );
    }
    case 'captions': {
      const p = produced.captionsConfiguration;
      if (!p) return false;
      const s = stored?.captionsConfiguration;
      // Unlike logo, a template usually stores no explicit caption override —
      // `verticalPosition`/`horizontalPosition` are null and the captions are
      // applied at the karaoke preset's own `yPos`/`xPos` (same 0–100 percent
      // units the renderer reports). Fall back to those so a live move off the
      // preset position is still detected as a deviation.
      const baselineVertical = s?.verticalPosition ?? s?.captionsConfig?.yPos;
      const baselineHorizontal =
        s?.horizontalPosition ?? s?.captionsConfig?.xPos;
      return (
        valueDiffers(p.verticalPosition, baselineVertical) ||
        valueDiffers(p.horizontalPosition, baselineHorizontal) ||
        valueDiffers(p.fontSize, s?.fontSize) ||
        stringDiffers(p.horizontalAlignment, s?.horizontalAlignment)
      );
    }
    case 'nameCard': {
      // The collector already nulls placementOverride when it matches the
      // composition's per-AR baseline, so a non-null value is a real deviation.
      return produced.namecardConfiguration?.placementOverride != null;
    }
    default:
      return false;
  }
}

// =============================================================================
// Enabled-only change builder
// =============================================================================

/**
 * Applies an enabled-only toggle for a single element type.
 * Preserves existing config values; only the `enabled` flag changes.
 */
export function applyEnabledOnlyChange(
  elementType: VideoTemplateElementType,
  enabled: boolean,
  templateConfig: VideoTemplateConfig | null,
  accumulators: ConfigAccumulators,
): void {
  switch (elementType) {
    case 'logo': {
      const existingLogo = templateConfig?.logoConfiguration;
      const logoConfig: LogoConfiguration = {
        enabled,
        lockPosition: existingLogo?.lockPosition ?? false,
        logo: existingLogo?.logo ?? null,
        opacity: existingLogo?.opacity ?? null,
        placementX: existingLogo?.placementX ?? null,
        placementY: existingLogo?.placementY ?? null,
        width: existingLogo?.width ?? null,
        height: existingLogo?.height ?? null,
        fit: existingLogo?.fit ?? null,
      };
      accumulators.repoConfigs.logoConfiguration = logoConfig;
      accumulators.localOverrides.logoConfiguration = logoConfig;
      break;
    }

    case 'captions': {
      const existingCaptions = templateConfig?.captionsConfiguration;
      const captionsConfig: CaptionsConfiguration = {
        enabled,
        lockPosition: existingCaptions?.lockPosition ?? false,
        captionsConfig: existingCaptions?.captionsConfig ?? null,
        verticalPosition: existingCaptions?.verticalPosition ?? null,
        horizontalPosition: existingCaptions?.horizontalPosition ?? null,
        horizontalAlignment: existingCaptions?.horizontalAlignment ?? null,
        fontSize: existingCaptions?.fontSize ?? null,
      };
      accumulators.repoConfigs.captionsConfiguration = {
        enabled,
        lockPosition: existingCaptions?.lockPosition ?? false,
        captionsConfig: existingCaptions?.captionsConfig?.id ?? null,
        verticalPosition: captionsConfig.verticalPosition,
        horizontalPosition: captionsConfig.horizontalPosition,
        horizontalAlignment: captionsConfig.horizontalAlignment,
        fontSize: captionsConfig.fontSize,
      };
      accumulators.localOverrides.captionsConfiguration = captionsConfig;
      break;
    }

    case 'nameCard': {
      const existingNamecard = templateConfig?.namecardConfiguration;
      const namecardConfig: NamecardConfiguration = {
        enabled,
        lockPosition: existingNamecard?.lockPosition ?? false,
        compositionTemplate: existingNamecard?.compositionTemplate ?? null,
        placementOverride: existingNamecard?.placementOverride ?? null,
      };
      accumulators.repoConfigs.namecardConfiguration = {
        enabled,
        lockPosition: existingNamecard?.lockPosition ?? false,
        compositionTemplate: existingNamecard?.compositionTemplate?.id ?? null,
        placementOverride: existingNamecard?.placementOverride ?? null,
      };
      accumulators.localOverrides.namecardConfiguration = namecardConfig;
      break;
    }

    case 'introCard': {
      const existingIntro = templateConfig?.introCardConfiguration;
      const introConfig: CardConfiguration = {
        enabled,
        lockPosition: existingIntro?.lockPosition ?? false,
        compositionTemplate: existingIntro?.compositionTemplate ?? null,
      };
      accumulators.repoConfigs.introCardConfiguration = {
        enabled,
        lockPosition: existingIntro?.lockPosition ?? false,
        compositionTemplate: existingIntro?.compositionTemplate?.id ?? null,
      };
      accumulators.localOverrides.introCardConfiguration = introConfig;
      break;
    }

    case 'endCard': {
      const existingEnd = templateConfig?.endCardConfiguration;
      const endConfig: CardConfiguration = {
        enabled,
        lockPosition: existingEnd?.lockPosition ?? false,
        compositionTemplate: existingEnd?.compositionTemplate ?? null,
      };
      accumulators.repoConfigs.endCardConfiguration = {
        enabled,
        lockPosition: existingEnd?.lockPosition ?? false,
        compositionTemplate: existingEnd?.compositionTemplate?.id ?? null,
      };
      accumulators.localOverrides.endCardConfiguration = endConfig;
      break;
    }

    case 'hook': {
      const existingHook = templateConfig?.hookConfiguration;
      const hookConfig: HookConfiguration = {
        enabled,
        lockPosition: existingHook?.lockPosition ?? false,
        compositionTemplate: existingHook?.compositionTemplate ?? null,
        duration: existingHook?.duration ?? null,
        placementOverride: existingHook?.placementOverride ?? null,
      };
      accumulators.repoConfigs.hookConfiguration = {
        ...hookConfig,
        compositionTemplate: existingHook?.compositionTemplate?.id ?? null,
      };
      accumulators.localOverrides.hookConfiguration = hookConfig;
      break;
    }
  }
}

// =============================================================================
// Lock-position change builder
// =============================================================================

/**
 * Applies a lock-position-only toggle for a single element type. Preserves the
 * existing config values (enabled / placement / composition & logo refs); only
 * the `lockPosition` flag changes. Used for a pure lock toggle in the update
 * modal that touches no enabled/placement/replacement state.
 */
export function applyLockPositionChange(
  elementType: VideoTemplateElementType,
  lockPosition: boolean,
  templateConfig: VideoTemplateConfig | null,
  accumulators: ConfigAccumulators,
): void {
  switch (elementType) {
    case 'logo': {
      const existingLogo = templateConfig?.logoConfiguration;
      const logoConfig: LogoConfiguration = {
        enabled: existingLogo?.enabled ?? true,
        lockPosition,
        logo: existingLogo?.logo ?? null,
        opacity: existingLogo?.opacity ?? null,
        placementX: existingLogo?.placementX ?? null,
        placementY: existingLogo?.placementY ?? null,
        width: existingLogo?.width ?? null,
        height: existingLogo?.height ?? null,
        fit: existingLogo?.fit ?? null,
      };
      accumulators.repoConfigs.logoConfiguration = logoConfig;
      accumulators.localOverrides.logoConfiguration = logoConfig;
      break;
    }

    case 'captions': {
      const existingCaptions = templateConfig?.captionsConfiguration;
      const captionsConfig: CaptionsConfiguration = {
        enabled: existingCaptions?.enabled ?? true,
        lockPosition,
        captionsConfig: existingCaptions?.captionsConfig ?? null,
        verticalPosition: existingCaptions?.verticalPosition ?? null,
        horizontalPosition: existingCaptions?.horizontalPosition ?? null,
        horizontalAlignment: existingCaptions?.horizontalAlignment ?? null,
        fontSize: existingCaptions?.fontSize ?? null,
      };
      accumulators.repoConfigs.captionsConfiguration = {
        enabled: captionsConfig.enabled,
        lockPosition,
        captionsConfig: existingCaptions?.captionsConfig?.id ?? null,
        verticalPosition: captionsConfig.verticalPosition,
        horizontalPosition: captionsConfig.horizontalPosition,
        horizontalAlignment: captionsConfig.horizontalAlignment,
        fontSize: captionsConfig.fontSize,
      };
      accumulators.localOverrides.captionsConfiguration = captionsConfig;
      break;
    }

    case 'nameCard': {
      const existingNamecard = templateConfig?.namecardConfiguration;
      const namecardConfig: NamecardConfiguration = {
        enabled: existingNamecard?.enabled ?? true,
        lockPosition,
        compositionTemplate: existingNamecard?.compositionTemplate ?? null,
        placementOverride: existingNamecard?.placementOverride ?? null,
      };
      accumulators.repoConfigs.namecardConfiguration = {
        enabled: namecardConfig.enabled,
        lockPosition,
        compositionTemplate: existingNamecard?.compositionTemplate?.id ?? null,
        placementOverride: existingNamecard?.placementOverride ?? null,
      };
      accumulators.localOverrides.namecardConfiguration = namecardConfig;
      break;
    }

    case 'introCard':
    case 'endCard': {
      const configKey =
        elementType === 'introCard'
          ? 'introCardConfiguration'
          : 'endCardConfiguration';
      const existingCard = templateConfig?.[configKey];
      const cardConfig: CardConfiguration = {
        enabled: existingCard?.enabled ?? true,
        lockPosition,
        compositionTemplate: existingCard?.compositionTemplate ?? null,
      };
      accumulators.repoConfigs[configKey] = {
        enabled: cardConfig.enabled,
        lockPosition,
        compositionTemplate: existingCard?.compositionTemplate?.id ?? null,
      };
      accumulators.localOverrides[configKey] = cardConfig;
      break;
    }

    case 'hook': {
      const existingHook = templateConfig?.hookConfiguration;
      const hookConfig: HookConfiguration = {
        enabled: existingHook?.enabled ?? true,
        lockPosition,
        compositionTemplate: existingHook?.compositionTemplate ?? null,
        duration: existingHook?.duration ?? null,
        placementOverride: existingHook?.placementOverride ?? null,
      };
      accumulators.repoConfigs.hookConfiguration = {
        ...hookConfig,
        compositionTemplate: existingHook?.compositionTemplate?.id ?? null,
      };
      accumulators.localOverrides.hookConfiguration = hookConfig;
      break;
    }
  }
}

// =============================================================================
// Replacement builders
// =============================================================================

type ReplacementInput = {
  templateCtx: TemplateConfigContext;
  editorCtx: EditorElementContext;
  replacedId: string;
};

/**
 * Merges a card replacement (introCard / endCard / nameCard) into the
 * accumulators. The replacedId is the new composition template ID.
 */
export function mergeCardReplacement(
  elementType: 'introCard' | 'endCard' | 'nameCard' | 'hook',
  { templateCtx, replacedId }: ReplacementInput,
  accumulators: ConfigAccumulators,
): void {
  const { activeTemplate, getCompositionById } = templateCtx;
  const configKey =
    elementType === 'introCard'
      ? 'introCardConfiguration'
      : elementType === 'endCard'
        ? 'endCardConfiguration'
        : elementType === 'hook'
          ? 'hookConfiguration'
          : 'namecardConfiguration';

  const existingConfig = activeTemplate.configuration?.[configKey];

  if (elementType === 'nameCard') {
    const nc = existingConfig as NamecardConfiguration | null | undefined;
    accumulators.repoConfigs.namecardConfiguration = {
      ...((accumulators.repoConfigs.namecardConfiguration as Record<
        string,
        unknown
      >) ?? {}),
      enabled:
        (
          accumulators.repoConfigs.namecardConfiguration as Record<
            string,
            unknown
          >
        )?.enabled ??
        nc?.enabled ??
        true,
      lockPosition:
        (
          accumulators.repoConfigs.namecardConfiguration as Record<
            string,
            unknown
          >
        )?.lockPosition ??
        nc?.lockPosition ??
        false,
      compositionTemplate: replacedId,
      placementOverride:
        (
          accumulators.repoConfigs.namecardConfiguration as Record<
            string,
            unknown
          >
        )?.placementOverride ??
        nc?.placementOverride ??
        null,
    };
    accumulators.localOverrides.namecardConfiguration = {
      ...(accumulators.localOverrides.namecardConfiguration ?? {}),
      enabled:
        accumulators.localOverrides.namecardConfiguration?.enabled ??
        nc?.enabled ??
        true,
      lockPosition:
        accumulators.localOverrides.namecardConfiguration?.lockPosition ??
        nc?.lockPosition ??
        false,
      compositionTemplate: getCompositionById?.(replacedId) ?? null,
      placementOverride:
        accumulators.localOverrides.namecardConfiguration?.placementOverride ??
        nc?.placementOverride ??
        null,
    };
  } else {
    const cc = existingConfig as CardConfiguration | null | undefined;
    const repoConfig = {
      ...((accumulators.repoConfigs[configKey] as Record<string, unknown>) ??
        {}),
      enabled:
        (accumulators.repoConfigs[configKey] as Record<string, unknown>)
          ?.enabled ??
        cc?.enabled ??
        true,
      lockPosition:
        (accumulators.repoConfigs[configKey] as Record<string, unknown>)
          ?.lockPosition ??
        cc?.lockPosition ??
        false,
      compositionTemplate: replacedId,
    };
    const localConfig: CardConfiguration = {
      ...((accumulators.localOverrides[configKey] as CardConfiguration) ?? {}),
      enabled:
        (accumulators.localOverrides[configKey] as CardConfiguration)
          ?.enabled ??
        cc?.enabled ??
        true,
      lockPosition:
        (accumulators.localOverrides[configKey] as CardConfiguration)
          ?.lockPosition ??
        cc?.lockPosition ??
        false,
      compositionTemplate: getCompositionById?.(replacedId) ?? null,
    };
    if (elementType === 'introCard') {
      accumulators.repoConfigs.introCardConfiguration = repoConfig;
      accumulators.localOverrides.introCardConfiguration = localConfig;
    } else {
      accumulators.repoConfigs.endCardConfiguration = repoConfig;
      accumulators.localOverrides.endCardConfiguration = localConfig;
    }
  }
}

/**
 * Merges a captions replacement into the accumulators.
 * Resolves the replaced karaoke template ID to the full object for local state.
 */
export function mergeCaptionsReplacement(
  { templateCtx, editorCtx, replacedId }: ReplacementInput,
  accumulators: ConfigAccumulators,
): void {
  const { activeTemplate } = templateCtx;
  const existingCaptions = activeTemplate.configuration?.captionsConfiguration;
  const existingRepo = accumulators.repoConfigs.captionsConfiguration as
    | Record<string, unknown>
    | undefined;
  const existingLocal = accumulators.localOverrides.captionsConfiguration;

  // Resolve the replaced karaoke template ID to its DatoCMS record, so the local
  // override holds the same shape a fetched template does. Resolving against
  // `brandingTemplates` instead would store a display copy whose colors are
  // already strings, and re-applying that (Reset) ran them through
  // `transformKaraokeConfigToTemplate` a second time, yielding
  // `rgba(undefined, …)` — captions rendered black until the next page load.
  const resolvedRecord = editorCtx.resolveKaraokeBrandingRecord(replacedId);

  // The link field takes the DatoCMS record ID. It is deliberately not the id the
  // UI selected by: a global preset is keyed by `presetId` there, and a slug like
  // "blackAndWhite" is not a valid link target (the CMA rejects the whole save).
  const captionsConfigRecordId = resolvedRecord?.id ?? null;

  accumulators.repoConfigs.captionsConfiguration = {
    ...(existingRepo ?? {}),
    enabled: existingRepo?.enabled ?? existingCaptions?.enabled ?? true,
    lockPosition:
      existingRepo?.lockPosition ?? existingCaptions?.lockPosition ?? false,
    captionsConfig: captionsConfigRecordId,
    verticalPosition:
      existingRepo?.verticalPosition ??
      existingCaptions?.verticalPosition ??
      null,
    horizontalPosition:
      existingRepo?.horizontalPosition ??
      existingCaptions?.horizontalPosition ??
      null,
    horizontalAlignment:
      existingRepo?.horizontalAlignment ??
      existingCaptions?.horizontalAlignment ??
      null,
    fontSize: existingRepo?.fontSize ?? existingCaptions?.fontSize ?? null,
  };

  accumulators.localOverrides.captionsConfiguration = {
    ...(existingLocal ?? {}),
    enabled: existingLocal?.enabled ?? existingCaptions?.enabled ?? true,
    lockPosition:
      existingLocal?.lockPosition ?? existingCaptions?.lockPosition ?? false,
    captionsConfig: resolvedRecord ?? existingCaptions?.captionsConfig ?? null,
    verticalPosition:
      existingLocal?.verticalPosition ??
      existingCaptions?.verticalPosition ??
      null,
    horizontalPosition:
      existingLocal?.horizontalPosition ??
      existingCaptions?.horizontalPosition ??
      null,
    horizontalAlignment:
      existingLocal?.horizontalAlignment ??
      existingCaptions?.horizontalAlignment ??
      null,
    fontSize: existingLocal?.fontSize ?? existingCaptions?.fontSize ?? null,
  };
}

/**
 * Merges a logo replacement into the accumulators.
 * Uses `resolveLogoUploadId` to find the upload ID from multiple sources.
 * Builds one config object and spreads it into both repo and local to avoid duplication.
 * Returns `'skip'` if no upload ID is available.
 */
export function mergeLogoReplacement(
  { templateCtx, replacedId }: ReplacementInput,
  accumulators: ConfigAccumulators,
): 'skip' | void {
  const { activeTemplate, activeTemplateSettings } = templateCtx;

  const logoUploadId = resolveLogoUploadId(
    activeTemplateSettings.replacedLogoUploadId,
    activeTemplate.configuration?.logoConfiguration?.logo?.id,
  );
  if (!logoUploadId) {
    logger.warn('Logo replacement skipped — no upload ID available');
    return 'skip';
  }

  const existingLogo = activeTemplate.configuration?.logoConfiguration;
  const existingRepoLogo = accumulators.repoConfigs.logoConfiguration as
    | Record<string, unknown>
    | undefined;
  const existingLocalLogo = accumulators.localOverrides.logoConfiguration;

  // Build one merged config and spread into both targets to avoid duplication
  const logoRef = { id: logoUploadId, url: replacedId };

  const mergedFromRepo = {
    ...(existingRepoLogo ?? {}),
    enabled: existingRepoLogo?.enabled ?? existingLogo?.enabled ?? true,
    lockPosition:
      existingRepoLogo?.lockPosition ?? existingLogo?.lockPosition ?? false,
    logo: logoRef,
    opacity: existingRepoLogo?.opacity ?? existingLogo?.opacity ?? null,
    placementX:
      existingRepoLogo?.placementX ?? existingLogo?.placementX ?? null,
    placementY:
      existingRepoLogo?.placementY ?? existingLogo?.placementY ?? null,
    width: existingRepoLogo?.width ?? existingLogo?.width ?? null,
    height: existingRepoLogo?.height ?? existingLogo?.height ?? null,
    fit: existingRepoLogo?.fit ?? existingLogo?.fit ?? null,
  };

  const mergedFromLocal = {
    ...(existingLocalLogo ?? {}),
    enabled: existingLocalLogo?.enabled ?? existingLogo?.enabled ?? true,
    lockPosition:
      existingLocalLogo?.lockPosition ?? existingLogo?.lockPosition ?? false,
    logo: logoRef,
    opacity: existingLocalLogo?.opacity ?? existingLogo?.opacity ?? null,
    placementX:
      existingLocalLogo?.placementX ?? existingLogo?.placementX ?? null,
    placementY:
      existingLocalLogo?.placementY ?? existingLogo?.placementY ?? null,
    width: existingLocalLogo?.width ?? existingLogo?.width ?? null,
    height: existingLocalLogo?.height ?? existingLogo?.height ?? null,
    fit: existingLocalLogo?.fit ?? existingLogo?.fit ?? null,
  };

  accumulators.repoConfigs.logoConfiguration = mergedFromRepo;
  accumulators.localOverrides.logoConfiguration = mergedFromLocal;
}
